import React from 'react';
import { useSelector } from 'react-redux';

import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import { filterOptions } from '../../assets/publications/publicationsFiltersOptions';
import { screenWidthSettings } from '../../constants';
import {
  filterPublications,
  searchPublications,
} from '../../features/publicationsSlice';
import {
  PublicationsAppliedFilters,
  PublicationsState,
  State,
} from '../../types';
import DropdownSelect from '../DropdownSelect';
import SearchBar from '../SearchBar';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    title: {
      fontFamily: "'Titillium Web', 'Helvetica', 'Arial', sans-serif",
      fontWeight: 400,
    },
    accordion: {
      width: '100%',
    },
    accordionSummary: {
      backgroundColor: '#E6E6E6',
    },
    accordionContent: {
      backgroundColor: '#F5F5F5',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    refineResults: {
      backgroundColor: 'white',
      padding: '2rem 0 0 0',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    refineResultsTitle: {
      width: '100%',
      maxWidth: screenWidthSettings.laptopScreenSize,
      padding: '0 1rem 0 1rem',
      display: 'flex',
      alignItems: 'center',
    },
    features: {
      padding: '0 1rem 0 1rem',
      width: '100%',
      maxWidth: screenWidthSettings.laptopScreenSize,
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      alignItems: 'flex-end',
      [theme.breakpoints.down(425)]: {
        flexDirection: 'column',
        alignItems: 'center',
      },
    },
    filterAndSort: {
      display: 'flex',
      flexWrap: 'wrap',
    },
    dropdown: {
      margin: '10px 20px 10px 0',
      [theme.breakpoints.down(769)]: {
        width: '100%',
        maxWidth: screenWidthSettings.searchBarWidth,
        marginRight: 0,
      },
    },
    searchBarContainer: {
      margin: '10px 0 10px 0',
      [theme.breakpoints.down(769)]: {
        width: '100%',
        maxWidth: screenWidthSettings.searchBarWidth,
      },
    },
    divider: {
      width: '96%',
      maxWidth: '990px',
      marginTop: '2rem',
      borderTop: '2px solid black',
    },
  })
);

const PublicationsRefineResults = () => {
  const classes = useStyles();
  const isMobileScreen = useMediaQuery(
    `(max-width:${screenWidthSettings.mobileScreenSize}px)`
  );
  const isTabletScreen = useMediaQuery(
    `(max-width:${screenWidthSettings.tabletScreenSize}px)`
  );
  const pubState = useSelector(
    (state: State): PublicationsState => state.publicationsReducer
  );
  const currentFilters: PublicationsAppliedFilters = pubState.appliedFilters;
  const currentSearchQuery: string = pubState.searchQuery;

  //if screen size is tablet or smaller, return contents inside an accordion
  if (isTabletScreen) {
    return (
      <Accordion className={classes.accordion} id='publications-refine-results'>
        <AccordionSummary
          className={classes.accordionSummary}
          expandIcon={<ExpandMoreIcon />}
          aria-controls='refine-results-filters'
          id='publication-refine-results-header'
        >
          <Typography
            align='left'
            variant={isMobileScreen ? 'h6' : 'h5'}
            className={classes.title}
            component='h2'
          >
            Refine Results
          </Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.accordionContent}>
          {Object.entries(currentFilters).map(([key, value]) => (
            <div className={classes.dropdown} key={key}>
              <DropdownSelect
                actionCreator={filterPublications}
                dropdownName={key}
                label={key}
                options={filterOptions[key]}
                selectedOption={value}
                width='100%'
              />
            </div>
          ))}
          <div className={classes.searchBarContainer}>
            <SearchBar
              placeholder='Search publications'
              actionCreator={searchPublications}
              renderLabel={true}
              searchQuery={currentSearchQuery}
            />
          </div>
        </AccordionDetails>
      </Accordion>
    );
  }

  return (
    <div className={classes.refineResults} id='publications-refine-results'>
      <div className={classes.refineResultsTitle}>
        <Typography
          align='left'
          variant={isMobileScreen ? 'h6' : 'h5'}
          className={classes.title}
          component='h2'
        >
          Refine Results
        </Typography>
      </div>
      <div className={classes.features}>
        <div className={classes.filterAndSort}>
          {Object.entries(currentFilters).map(([key, value]) => (
            <div className={classes.dropdown} key={key}>
              <DropdownSelect
                actionCreator={filterPublications}
                dropdownName={key}
                label={key}
                options={filterOptions[key]}
                selectedOption={value}
                width='100%'
              />
            </div>
          ))}
        </div>
        <div className={classes.searchBarContainer}>
          <SearchBar
            placeholder='Search publications'
            actionCreator={searchPublications}
            renderLabel={true}
            searchQuery={currentSearchQuery}
          />
        </div>
      </div>
      <hr className={classes.divider} />
    </div>
  );
};

export default PublicationsRefineResults;
